import { DefaultButton, IDropdownOption, PrimaryButton, Stack } from "@fluentui/react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { Location, useLocationSchema } from "../locationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { ControlledTextField } from "../../../common/components/form/ControlledTextField";
import { nameof } from "../../../common/utilities";
import { useCreateLocation, useUpdateLocation } from "../locationsQueries";
import { RegionDetailDTO, SubRegionDetailDTO } from "../../../services/contactReportsApi";
import { ControlledDropdown } from "../../../common/components/form/ControlledDropdown";
import { useMemo } from "react";
import { useTranslator } from "../../../common/state/translatorState";
import useErrorHandler from "../../../common/hooks/useErrorHandler";
import useToastNotification from "../../../common/hooks/useToastNotification";

const Container = styled.div`
  min-width: 24rem;
  min-height: 7rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

type ParentDropdownOption = IDropdownOption<RegionDetailDTO | SubRegionDetailDTO>;

type Props = {
  location: Location;
  parents?: RegionDetailDTO[] | SubRegionDetailDTO[];
  onCancel: () => any;
};

export default function LocationEditForm({ location, parents, onCancel }: Props) {
  const t = useTranslator();
  const toast = useToastNotification();
  const locationSchema = useLocationSchema();
  const { mutate: createLocation, isLoading: isCreating } = useCreateLocation();
  const { mutate: updateLocation, isLoading: isUpdating } = useUpdateLocation();

  const isLoading = isCreating || isUpdating;
  const hasParents = Array.isArray(parents);

  const parentOptions = useMemo<ParentDropdownOption[]>(
    () => (Array.isArray(parents) ? parents.map((p): ParentDropdownOption => ({ key: p.id, text: p.name, data: p })) : []),
    [parents]
  );

  const { control, handleSubmit } = useForm<Location>({
    defaultValues: location,
    resolver: zodResolver(locationSchema),
  });

  const onSubmit = handleSubmit((data) => {
    if (data.isEdit) {
      updateLocation(
        { location: data },
        {
          onSuccess: () => {
            toast.showSuccess(t.Components.ToastMessage.RecordUpdated.Title);
            onCancel();
          },
        }
      );
    } else {
      createLocation(
        { location: data },
        {
          onSuccess: () => {
            toast.showSuccess(t.Components.ToastMessage.RecordCreated.Title);
            onCancel();
          },
        }
      );
    }
  });

  return (
    <Container>
      {hasParents && (
        <ControlledDropdown
          className="mb-4"
          name={nameof<Location>("parentId")}
          control={control}
          options={parentOptions}
          label={location.type === "country" ? t.LocationsEditor.Field.SubRegion.Label : t.LocationsEditor.Field.Region.Label}
        />
      )}
      <ControlledTextField name={nameof<Location>("name")} control={control} label={t.LocationsEditor.Field.Name.Label} autoFocus />
      <Stack horizontal className="mt-4 gap-4 flex-row-reverse">
        <DefaultButton disabled={isLoading} text={t.Components.Button.Cancel.Label} onClick={onCancel} />
        <PrimaryButton disabled={isLoading} text={t.Components.Button.Save.Label} onClick={onSubmit} />
      </Stack>
    </Container>
  );
}
